/**
 * AppFooter — Platform-branded footer for app mode routes.
 *
 * Rendered in the "footer" VLT slot. On platform module routes, replaces
 * the CMS footer links/colophon with platform branding. On CMS content
 * routes, renders nothing (VLT footer shows normally).
 *
 * Footer links are filtered by user role to avoid exposing links to pages
 * the user cannot access. Role is provided by the shared useRole() hook
 * (BUG-2026-076).
 *
 * See ARCH-2026-008 Phase A, TEST-PERM-001.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useRole } from '../hooks/useRole';

const isAppRoute = (pathname) =>
  pathname === '/' ||
  pathname.startsWith('/qms') ||
  pathname.startsWith('/polyglot') ||
  pathname.startsWith('/docs') ||
  pathname.startsWith('/documents') ||
  pathname.startsWith('/policies') ||
  pathname.startsWith('/api-docs');

const footerLinks = [
  { to: '/polyglot', label: 'Polyglot', roles: ['user', 'contributor', 'admin'] },
  { to: '/qms', label: 'QMS', roles: ['admin'] },
  { to: '/docs', label: 'Docs', roles: ['contributor', 'admin'] },
  { to: '/policies', label: 'Policies', roles: ['user', 'contributor', 'admin'] },
  { to: '/en', label: 'Content', roles: ['contributor', 'admin'] },
];

const AppFooter = () => {
  const { pathname } = useLocation();
  const { role } = useRole();
  const loggedIn = role !== 'public' && role !== 'loading';

  if (!isAppRoute(pathname)) return null;

  const visibleLinks = loggedIn
    ? footerLinks.filter((l) => l.roles.includes(role))
    : [{ to: '/polyglot', label: 'Polyglot' }, { to: '/policies', label: 'Policies' }];

  return (
    <div style={{ textAlign: 'center', padding: '2em 0', color: '#888', fontSize: '0.9em' }}>
      <div style={{ marginBottom: '0.5em' }}>
        {visibleLinks.map((l, i) => (
          <span key={l.to}>
            {i > 0 && <span style={{ margin: '0 0.5em' }}>|</span>}
            <Link to={l.to} style={{ color: '#888', textDecoration: 'none' }}>{l.label}</Link>
          </span>
        ))}
      </div>
      <div>
        Ljusa Tider AB &mdash; Built with{' '}
        <a href="https://nickcms.org" target="_blank" rel="noopener noreferrer" style={{ color: '#888' }}>
          Nick CMS
        </a>
        {' & '}
        <a href="https://voltocms.com" target="_blank" rel="noopener noreferrer" style={{ color: '#888' }}>
          Volto
        </a>
      </div>
    </div>
  );
};

export default AppFooter;
